import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Breadcrumbs from "../components/Breadcrumbs";
import LaTeXReportsPanel from "../components/LaTeXReportsPanel";

export default function AssignmentDetailPage({ token, user, isCreating = false }) {
  const { assignmentId } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [learnerSubmission, setLearnerSubmission] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [fileToSubmit, setFileToSubmit] = useState(null);
  const [assignmentContentTypeId, setAssignmentContentTypeId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [classes, setClasses] = useState([]);
  const [rubrics, setRubrics] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token || !user || !user.user_profile) {
      setLoading(false);
      return;
    }

    // If in creation mode, skip data fetching and set up for creation
    if (isCreating) {
      setIsEditMode(true);
      setEditData({
        title: '',
        description: '',
        due_date: '',
        max_points: 100,
        class_assigned: '',
        assignment_type: 'practical',
        submission_format: 'pdf',
        rubric: ''
      });
      setLoading(false);
      return;
    }

    if (!assignmentId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError("");
      setAssignment(null);
      setSubmissions([]);
      setLearnerSubmission(null);

      axios
        .get(`/api/assignments/${assignmentId}/`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => {
          setAssignment(res.data);
          setEditData({
            title: res.data.title,
            description: res.data.description,
            due_date: res.data.due_date ? res.data.due_date.slice(0, 16) : '',
            max_points: res.data.max_points,
            class_assigned: res.data.class_assigned,
            assignment_type: res.data.assignment_type || 'practical',
            submission_format: res.data.submission_format || 'pdf',
            rubric: res.data.rubric || ''
          });
        })
        .catch(() => setError("Failed to fetch assignment details"))
        .finally(() => {});

      if (user.user_profile.role === "teacher") {
        axios
          .get(`/api/submissions/?assessment=${assignmentId}`, {
            headers: { Authorization: `Token ${token}` },
          })
          .then((res) => {
            const fetchedSubmissions = res.data.results || res.data || [];
            setSubmissions(fetchedSubmissions);
          })
          .catch(() =>
            setError((prev) => prev + " Failed to fetch submissions for teacher.")
          )
          .finally(() => setLoading(false));
      } else if (user.user_profile.role === "student") {
        axios
          .get(`/api/submissions/?assessment=${assignmentId}&learner=${user.id}`, {
            headers: { Authorization: `Token ${token}` },
          })
          .then((res) => {
            const studentSubmissions = res.data.results || res.data || [];
            if (studentSubmissions.length > 0) {
              setLearnerSubmission(studentSubmissions[0]);
            }
          })
          .catch(() =>
            setError((prev) => prev + " Failed to fetch learner's submission.")
          )
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, assignmentId, user, isCreating]);

  // Fetch teacher's classes and rubrics when edit mode is enabled
  useEffect(() => {
    if (isEditMode && user?.user_profile?.role === "teacher" && token) {
      // Fetch classes
      axios
        .get(`/api/school-classes/?teacher=${user.id}`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => {
          setClasses(res.data.results || res.data || []);
        })
        .catch((err) => {
          console.error("Failed to fetch classes:", err);
        });
      
      // Fetch rubrics
      axios
        .get(`/api/rubrics/`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => {
          setRubrics(res.data.results || res.data || []);
        })
        .catch((err) => {
          console.error("Failed to fetch rubrics:", err);
        });
    }
  }, [isEditMode, user, token]);

  const handleFileSelect = (event) => {
    setFileToSubmit(event.target.files[0]);
  };

  const handleSubmitAssignment = async () => {
    if (
      !fileToSubmit ||
      !assignmentId ||
      !user ||
      !user.id ||
      !assignmentContentTypeId
    ) {
      setError(
        "No file selected or missing critical information (like ContentType ID) for submission."
      );
      return;
    }
    setError("");
    setLoading(true);

    const formData = new FormData();
    formData.append("file", fileToSubmit);
    formData.append("object_id", assignmentId);
    formData.append("content_type", assignmentContentTypeId);

    try {
      const response = await axios.post("/api/submissions/", formData, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setLearnerSubmission(response.data);
      setFileToSubmit(null);
      alert("Assignment submitted successfully!");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit assignment.");
      console.error("Submission error:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    setUpdateLoading(true);
    setError("");

    try {
      const payload = {
        title: editData.title,
        description: editData.description,
        due_date: new Date(editData.due_date).toISOString(),
        max_points: parseInt(editData.max_points),
        class_assigned: parseInt(editData.class_assigned),
        assignment_type: editData.assignment_type || 'practical',
        submission_format: editData.submission_format || 'pdf',
        rubric: editData.rubric ? parseInt(editData.rubric) : null
      };

      let response;
      if (isCreating) {
        // Create new assignment
        response = await axios.post(
          `/api/assignments/`,
          payload,
          {
            headers: { Authorization: `Token ${token}` },
          }
        );
        alert("Assignment created successfully!");
        // Navigate to the new assignment's detail page
        navigate(`/assignments/${response.data.id}`);
      } else {
        // Update existing assignment
        response = await axios.patch(
          `/api/assignments/${assignmentId}/`,
          payload,
          {
            headers: { Authorization: `Token ${token}` },
          }
        );
        setAssignment(response.data);
        setIsEditMode(false);
        alert("Assignment updated successfully!");
        
        // If class was changed, navigate to the new class detail page
        if (!isCreating && assignment && response.data.class_assigned !== assignment?.class_assigned) {
          navigate(`/classes/${response.data.class_assigned}`);
        }
      }
    } catch (err) {
      console.error("Update error:", err);
      setError(err.response?.data?.detail || "Failed to update assignment");
    } finally {
      setUpdateLoading(false);
    }
  };

  const cancelEdit = () => {
    if (isCreating) {
      // Navigate back to assignments list when canceling creation
      navigate('/assignments');
      return;
    }
    
    setIsEditMode(false);
    setEditData({
      title: assignment?.title || '',
      description: assignment?.description || '',
      due_date: assignment?.due_date ? assignment.due_date.slice(0, 16) : '',
      max_points: assignment?.max_points || 100,
      class_assigned: assignment?.class_assigned || '',
      assignment_type: assignment?.assignment_type || 'practical',
      submission_format: assignment?.submission_format || 'pdf',
      rubric: assignment?.rubric || ''
    });
    setError("");
  };

  if (!token || !user || !user.user_profile) return <p>Loading user data...</p>;
  if (loading) return <p>Loading assignment details...</p>;
  if (!assignment && !isCreating)
    return <p>Assignment not found or error loading details. {error}</p>;

  const isTeacher = user.user_profile.role === "teacher";
  const isStudent = user.user_profile.role === "student";

  const deadlinePassed =
    assignment && new Date(assignment.due_date) < new Date();

  const breadcrumbPaths = isCreating ? [
    { name: 'Dashboard', path: '/' },
    { name: 'Assignments', path: '/assignments' },
    { name: 'New Assignment', path: '/assignments/new' }
  ] : [
    { name: 'Dashboard', path: '/' },
    { name: 'Classes', path: '/classes' },
    { name: assignment?.class_details?.name || 'Class', path: `/classes/${assignment?.class_assigned}` },
    { name: assignment?.title || 'Assignment', path: `/assignments/${assignmentId}` }
  ];

  const getClassById = (classId) => {
    return classes.find(c => c.id === parseInt(classId));
  };

  return (
    <div style={{ padding: "20px" }}>
      <Breadcrumbs paths={breadcrumbPaths} />
      
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "30px" 
      }}>
        <h1 style={{ margin: 0, fontSize: "2rem", color: "#333" }}>
          {isCreating ? "Create New Assignment" : (isEditMode ? "Edit Assignment" : assignment?.title)}
        </h1>
        
        {isTeacher && !isEditMode && !isCreating && (
          <button
            onClick={() => setIsEditMode(true)}
            style={{
              padding: "12px 24px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            ✏️ Edit Assignment
          </button>
        )}
      </div>

      {error && (
        <div style={{ 
          padding: "16px", 
          backgroundColor: "#f8d7da", 
          color: "#721c24",
          border: "1px solid #f5c6cb",
          borderRadius: "8px",
          marginBottom: "20px"
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {isEditMode && isTeacher ? (
        <div style={{
          backgroundColor: "#f8f9fa",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
          marginBottom: "30px"
        }}>
          <h3 style={{ marginTop: 0, marginBottom: "20px", color: "#495057" }}>
            Edit Assignment Details
          </h3>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500", 
              color: "#495057" 
            }}>
              Assignment Title *
            </label>
            <input
              type="text"
              name="title"
              value={editData.title || ''}
              onChange={handleEditChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box"
              }}
              required
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500", 
              color: "#495057" 
            }}>
              Description
            </label>
            <textarea
              name="description"
              value={editData.description || ''}
              onChange={handleEditChange}
              rows="4"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
                resize: "vertical"
              }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500", 
              color: "#495057" 
            }}>
              Due Date *
            </label>
            <input
              type="datetime-local"
              name="due_date"
              value={editData.due_date || ''}
              onChange={handleEditChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box"
              }}
              required
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500", 
              color: "#495057" 
            }}>
              Max Points *
            </label>
            <input
              type="number"
              name="max_points"
              value={editData.max_points || ''}
              onChange={handleEditChange}
              min="0"
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box"
              }}
              required
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500", 
              color: "#495057" 
            }}>
              Assign to Class *
            </label>
            <select
              name="class_assigned"
              value={editData.class_assigned || ''}
              onChange={handleEditChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                fontSize: "14px",
                backgroundColor: "white",
                boxSizing: "border-box"
              }}
              required
            >
              <option value="">Select a class...</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name} ({cls.subject})
                </option>
              ))}
            </select>
            {editData.class_assigned && !isCreating && assignment && editData.class_assigned !== assignment?.class_assigned && (
              <div style={{
                marginTop: "8px",
                padding: "8px 12px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#856404"
              }}>
                ⚠️ Assignment will be moved to "{getClassById(editData.class_assigned)?.name}" class
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                fontWeight: "500", 
                color: "#495057" 
              }}>
                Assignment Type *
              </label>
              <select
                name="assignment_type"
                value={editData.assignment_type || 'practical'}
                onChange={handleEditChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  boxSizing: "border-box"
                }}
                required
              >
                <option value="practical">Practical Assignment</option>
                <option value="theory">Theory Assignment</option>
                <option value="project">Project</option>
                <option value="test">Test</option>
                <option value="exam">Exam</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                fontWeight: "500", 
                color: "#495057" 
              }}>
                Submission Format *
              </label>
              <select
                name="submission_format"
                value={editData.submission_format || 'pdf'}
                onChange={handleEditChange}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  boxSizing: "border-box"
                }}
                required
              >
                <option value="pdf">PDF Document</option>
                <option value="docx">Word Document</option>
                <option value="zip">ZIP Archive</option>
                <option value="image">Image File</option>
                <option value="code">Code File</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500", 
              color: "#495057" 
            }}>
              Assessment Rubric
            </label>
            <select
              name="rubric"
              value={editData.rubric || ''}
              onChange={handleEditChange}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                fontSize: "14px",
                backgroundColor: "white",
                boxSizing: "border-box"
              }}
            >
              <option value="">No rubric assigned</option>
              {rubrics.map(rubric => (
                <option key={rubric.id} value={rubric.id}>
                  {rubric.name}
                </option>
              ))}
            </select>
            {editData.rubric && (
              <div style={{
                marginTop: "8px",
                padding: "8px 12px",
                backgroundColor: "#d4edda",
                border: "1px solid #c3e6cb",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#155724"
              }}>
                ✓ Rubric assigned - AI analysis will use this rubric's criteria
              </div>
            )}
          </div>

          <div style={{ 
            display: "flex", 
            gap: "12px", 
            paddingTop: "20px",
            borderTop: "1px solid #e9ecef"
          }}>
            <button
              onClick={handleSaveEdit}
              disabled={updateLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: updateLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
                opacity: updateLoading ? 0.7 : 1
              }}
            >
              {updateLoading ? "Saving..." : (isCreating ? "Create Assignment" : "Save Changes")}
            </button>
            <button
              onClick={cancelEdit}
              disabled={updateLoading}
              style={{
                padding: "12px 24px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: updateLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
                opacity: updateLoading ? 0.7 : 1
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          backgroundColor: "#fff",
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
          marginBottom: "30px"
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            <div>
              <h3 style={{ margin: "0 0 8px 0", color: "#495057", fontSize: "1.1rem" }}>
                Assignment Details
              </h3>
              <p style={{ margin: "8px 0", lineHeight: "1.6" }}>
                <strong>Description:</strong> {assignment?.description || "No description provided"}
              </p>
              <p style={{ margin: "8px 0", lineHeight: "1.6" }}>
                <strong>Max Points:</strong> {assignment?.max_points || "Not set"}
              </p>
            </div>
            <div>
              <h3 style={{ margin: "0 0 8px 0", color: "#495057", fontSize: "1.1rem" }}>
                Timeline & Class
              </h3>
              <p style={{ margin: "8px 0", lineHeight: "1.6" }}>
                <strong>Due Date:</strong> {assignment?.due_date ? new Date(assignment.due_date).toLocaleString() : "Not set"}
              </p>
              <p style={{ margin: "8px 0", lineHeight: "1.6" }}>
                <strong>Class:</strong> {assignment?.class_details?.name || "Unknown Class"}
              </p>
              <p style={{ margin: "8px 0", lineHeight: "1.6" }}>
                <strong>Subject:</strong> {assignment?.class_details?.subject || "Unknown Subject"}
              </p>
              <p style={{ margin: "8px 0", lineHeight: "1.6" }}>
                <strong>Rubric:</strong> {assignment?.rubric_details?.name || "No rubric assigned"}
              </p>
            </div>
          </div>
        </div>
      )}

      {isTeacher && !isCreating && assignment && (
        <>
          {/* Report Generation Panel */}
          <div style={{ marginBottom: "30px" }}>
            <LaTeXReportsPanel 
              assignmentId={assignmentId} 
              assignmentTitle={assignment?.title} 
            />
          </div>

          <h3>Submissions for this Assignment:</h3>
          {submissions.length === 0 && !error && (
            <div>No submissions found for this assignment.</div>
          )}
          <ul style={{ listStyle: "none", padding: 0 }}>
            {submissions.map((s, index) => (
              <li
                key={s.id}
                style={{
                  padding: "10px",
                  border: "1px solid #eee",
                  marginBottom: "8px",
                  borderRadius: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <strong>Learner:</strong>{" "}
                  {s.student_details?.username || s.student || "Unknown"} <br />
                  <strong>Status:</strong> {s.status} <br />
                  <strong>Submitted:</strong>{" "}
                  {s.submitted_at
                    ? new Date(s.submitted_at).toLocaleString()
                    : "N/A"}
                </div>
                <Link
                  to={`/submissions/${s.id}`}
                  state={{
                    submissionIds: submissions.map((sub) => sub.id),
                    currentSubmissionIndex: index,
                    classId: assignment?.class_assigned,
                    assignmentId: assignment?.id,
                    assignmentTitle: assignment?.title,
                    studentName: s.student_details?.username || s.student,
                  }}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#5C4033",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                    marginLeft: "10px",
                  }}
                >
                  Mark Submission
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {isStudent && (
        <div style={{ marginTop: "20px" }}>
          <h3>My Submission</h3>
          {loading && !learnerSubmission && (
            <p>Loading your submission details...</p>
          )}

          {learnerSubmission ? (
            <div>
              <p>
                <strong>Status:</strong> {learnerSubmission.status}
              </p>
              <p>
                <strong>Submitted At:</strong>{" "}
                {new Date(learnerSubmission.submitted_at).toLocaleString()}
              </p>
              {learnerSubmission.file && (
                <p>
                  <strong>File:</strong>{" "}
                  <a
                    href={learnerSubmission.file}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Submitted File
                  </a>
                </p>
              )}
              {learnerSubmission.status === "marked" && (
                <>
                  <p>
                    <strong>Score:</strong>{" "}
                    {learnerSubmission.teacher_score ??
                      learnerSubmission.ai_score ??
                      "Not graded"}
                  </p>
                  <p>
                    <strong>Feedback:</strong>{" "}
                    {learnerSubmission.feedback ||
                      learnerSubmission.ai_feedback ||
                      "No feedback provided."}
                  </p>
                </>
              )}
            </div>
          ) : deadlinePassed ? (
            <p style={{ color: "orange" }}>
              The deadline for this assignment has passed. You can no longer
              submit.
            </p>
          ) : (
            <div>
              <p>You have not submitted this assignment yet.</p>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                disabled={loading}
              />
              <button
                onClick={handleSubmitAssignment}
                disabled={loading || !fileToSubmit}
                style={{ marginTop: "10px" }}
              >
                {loading ? "Submitting..." : "Submit Assignment"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
